import classNames from 'classnames';
import React from 'react';
import { BookingEntity } from 'Models/Entities';
import alertToast from 'Util/ToastifyUtils';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { reportHandledError } from 'Util/_HumanWritten/Observability/observability';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import { BookingBreakdown } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/BookingBreakdown';
import { removeReturnBookingModal } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/RemoveReturnBookingModal';
import If from 'Views/Components/If/If';
import { observer } from 'mobx-react';
import { LinkedBookingPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { useBookingWizardPricingCacheStore } from '../../BookingWizardPricingCacheStore';
import { FreightBookingBreakdown } from './FreightBookingBreakdown';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { getBookingTypeFromTabSelected, getOneWayOrReturnBooking } from '../../BookingWizardHelpers';
import { BookingWizardCartFields } from '../../BookingWizardTypes';
import { ConfigurableLink } from '../../../../ConfigurableLink';

export type tripSummaryLocationType = 'cart' | 'sidebar' | 'booking-success' | 'check-in';

export interface TripBookingSummaryCardProps {
	wizardData?: BookingWizardData;
	booking: BookingEntity;
	cartFields: BookingWizardCartFields;
	count: number;
	bookingInfo?: LinkedBookingPricingResult;
	tripSummaryLocation: tripSummaryLocationType;
	selectedTrips?: SelectedTrips;
	setSelectedTrips?: (trips: SelectedTrips) => void;
	isAlteration: boolean;
	onRemoveBooking: (
		bookingId: string,
		removeAssociatedBooking: boolean,
		departureBooking: boolean,
	) => Promise<boolean>;
	onEditBooking: (
		cartFields: BookingWizardCartFields,
		departureBooking: boolean,
		bookingEntity: BookingEntity,
	) => Promise<void>;
	afterPayment: boolean;
	cartView: boolean;
	showQrCodes?: boolean;
	hideLinks?: boolean;
}

/**
 * @deprecated Superseded by `ClassBookingSummary/ClassBookingSummaryCard`, which sites with fare types use in
 * the cart, the sidebar, check-in and on the booking success page. Still used everywhere on a site without
 * fare types (temporarily) and for freight. Kept until those move across; do not build on it.
 */
function TripBookingSummaryCard({
	wizardData,
	booking,
	cartFields,
	count,
	bookingInfo,
	tripSummaryLocation,
	isAlteration,
	onRemoveBooking,
	onEditBooking,
	afterPayment,
	cartView,
	showQrCodes,
	hideLinks,
}: TripBookingSummaryCardProps) {
	const { getBookingPrice } = useBookingWizardPricingCacheStore();

	const shouldShowEditButtons = () => {
		return tripSummaryLocation === 'cart' && !isAlteration;
	};

	const canEditBooking = () => {
		return true;
	};

	const handleEdit = async (departureBooking: boolean) => {
		await onEditBooking(cartFields, departureBooking, booking);
	};

	const handleRemove = async (departureBooking: boolean) => {
		let removeReturn = false;

		const handleRemoveReturnTrip = async (value: boolean) => {
			removeReturn = value;
			const confirmed = await newConfirmModal(
				'Remove ferry ticket?',
				"This action will release your booking and can't be undone.",
				{
					cancelText: 'Back',
					confirmText: 'Confirm',
					shouldCloseOnOverlayClick: false,
				},
			);

			if (confirmed) {
				await onRemoveBooking(cartFields.wizardData.bookingToEdit ?? '', removeReturn, departureBooking);
			}
		};

		try {
			if (cartFields.departingJourney && cartFields.returningJourney) {
				await removeReturnBookingModal(departureBooking, (value: boolean) => handleRemoveReturnTrip(value));
			} else {
				await handleRemoveReturnTrip(true);
			}
		} catch (e) {
			// newConfirmModal rejects with no value when the user cancels or closes, which is the
			// ordinary way out of this flow — nothing to do. Anything else is a real failure of the
			// removal, which was previously dropped on the floor as an unhandled rejection.
			if (e !== undefined) {
				reportHandledError(e, { event: 'cart_remove_booking_failed' });
				alertToast('There was an error removing this booking.', 'error', 'Failed to remove booking');
			}
		}
	};

	const shouldShowPrice = () => {
		return (tripSummaryLocation === 'sidebar' || afterPayment) && !isCurrentBooking();
	};

	const isCurrentBooking = () => {
		return wizardData?.bookingToEdit === cartFields.wizardData.bookingToEdit;
	};

	const isFreightBooking = cartFields.wizardData?.tabSelected === 'freight';

	return (
		<div key={`${count}-booking-card`} className="booking-summary-card__inner">
			<div className={classNames('booking-summary-card__header', { 'show-price': shouldShowPrice() })}>
				{/* If after payment, displays a link to booking (using the booking human readable id)
				    Else, displays a booking count (E.g. Booking 1) */}
				<p className={classNames('booking-summary-card__type-and-number')}>
					{isAlteration
						? `${getBookingTypeFromTabSelected(cartFields.wizardData?.tabSelected)} Update`
						: `${getBookingTypeFromTabSelected(cartFields.wizardData?.tabSelected)} Booking`}{' '}
					{afterPayment && booking ? (
						<ConfigurableLink to={`/bookings/${booking.id}`} disabled={hideLinks}>
							#{booking.humanReadableId}
						</ConfigurableLink>
					) : (
						count + 1
					)}
					&nbsp;
					{shouldShowPrice() &&
						!afterPayment &&
						getOneWayOrReturnBooking(cartFields.wizardData.tripType === 'one way')}
				</p>
				{shouldShowPrice() && (
					<p className="line-item-price">
						{formatPriceDisplay(
							booking.id && !afterPayment
								? getBookingPrice(booking.id)
								: (bookingInfo?.departureBooking.totalPrice ?? 0) +
										(bookingInfo?.returnBooking?.totalPrice ?? 0),
							isAlteration,
						)}
					</p>
				)}
			</div>
			<If condition={afterPayment || cartView || (!cartView && isCurrentBooking())}>
				<If condition={!isFreightBooking}>
					{cartFields.departingJourney && (
						<BookingBreakdown
							key="departure-breakdown"
							cartData={cartFields}
							departureTrip
							bookingInfo={bookingInfo?.departureBooking}
							journey={cartFields.departingJourney}
							shouldShowEditButtons={shouldShowEditButtons()}
							canEditBooking={canEditBooking()}
							handleEdit={() => handleEdit(true)}
							handleRemove={() => handleRemove(true)}
							isAlteration={isAlteration}
							shouldShowPrice={shouldShowPrice()}
							afterPayment={afterPayment}
							isCurrentBooking={isCurrentBooking()}
							cartView={cartView}
							showQrCode={showQrCodes}
						/>
					)}
					<If
						condition={
							cartFields.wizardData.returningTicketId !== '' ||
							(cartFields.wizardData.tripType === 'return' &&
								wizardData?.returningTicketId !== '' &&
								wizardData?.returningTicketId !== undefined)
						}
					>
						<If condition={afterPayment}>
							<div
								className={classNames('booking-summary-card__header', {
									'show-price': shouldShowPrice(),
								})}
							>
								<p className={classNames('booking-summary-card__type-and-number')}>
									{isAlteration
										? `${getBookingTypeFromTabSelected(cartFields.wizardData?.tabSelected)} Update`
										: `${getBookingTypeFromTabSelected(cartFields.wizardData?.tabSelected)} Booking`}{' '}
									{afterPayment && booking ? (
										<ConfigurableLink
											to={`/bookings/${booking.returnBooking?.id}`}
											disabled={hideLinks}
										>
											#{booking.returnBooking?.humanReadableId}
										</ConfigurableLink>
									) : (
										count + 1
									)}
								</p>
							</div>
						</If>
						{cartFields.returningJourney && (
							<BookingBreakdown
								key="return-breakdown"
								cartData={cartFields}
								departureTrip={false}
								bookingInfo={bookingInfo?.returnBooking ?? undefined}
								journey={cartFields.returningJourney}
								shouldShowEditButtons={shouldShowEditButtons()}
								canEditBooking={canEditBooking()}
								handleEdit={() => handleEdit(false)}
								handleRemove={() => handleRemove(false)}
								isAlteration={isAlteration}
								shouldShowPrice={shouldShowPrice()}
								afterPayment={afterPayment}
								isCurrentBooking={isCurrentBooking()}
								cartView={cartView}
								showQrCode={showQrCodes}
							/>
						)}
					</If>
				</If>
				<If condition={isFreightBooking && isNotNullOrUndefined(cartFields.departingJourney)}>
					<FreightBookingBreakdown
						key="freight-breakdown"
						cartData={cartFields}
						departureTrip
						bookingInfo={bookingInfo?.departureBooking}
						journey={cartFields.departingJourney!}
						shouldShowEditButtons={shouldShowEditButtons()}
						canEditBooking={canEditBooking()}
						handleEdit={() => handleEdit(true)}
						handleRemove={() => handleRemove(true)}
						isAlteration={isAlteration}
						shouldShowPrice={shouldShowPrice()}
						afterPayment={afterPayment}
						isCurrentBooking={isCurrentBooking()}
						cartView={cartView}
						showQrCode={showQrCodes}
					/>
				</If>
			</If>
		</div>
	);
}

export default observer(TripBookingSummaryCard);
